import { useCurrentAddressInfo } from '@/hooks';
import request from '@/utils/request';
import { useMutation, useQuery } from 'react-query';
import { useCurrentPrimeIssueId, usePrimePostIssues } from '../IssueSelect/hooks';
import { setQueryAllFeedDetailByArticleId } from '../../hooks';
import { FeedArticleDetail } from '../..';
import produce from 'immer';
import { queryClient, toastError } from '@/utils';
import { isNumber } from 'lodash';
import { useMemo } from 'react';

export const usePrimePostVote = ({
  articleId,
  hasVoted,
  issueId,
}: {
  articleId: number;
  hasVoted: boolean;
  issueId: string;
}) => {
  const url = '/user/prime_post/vote';
  const { address: currentAddress } = useCurrentAddressInfo();

  return useMutation(
    () =>
      request.post<{ is_success: boolean }>(url, {
        is_vote: hasVoted,
        article_id: articleId,
        issue_id: issueId,
      }),
    {
      onSuccess: res => {
        if (res.is_success) {
          const handle = (pre?: FeedArticleDetail) => {
            if (pre?.prime_post_issue) {
              pre.prime_post_issue.has_voted = hasVoted;
            }
            if (pre?.article?.prime_post_issue) {
              pre.article.prime_post_issue.has_voted = hasVoted;
            }
          };
          setQueryAllFeedDetailByArticleId({
            currentAddress: currentAddress!,
            articleId,
            recipe: handle,
            includeQuote: true,
          });
        }

        const remainingVoteCountKey = ['/user/prime_post/remaining_vote', currentAddress, issueId];
        if (queryClient.getQueryData(remainingVoteCountKey)) {
          queryClient.setQueryData<{ remaining_count: number } | undefined>(
            remainingVoteCountKey,
            pre => {
              return produce(pre, draft => {
                if (isNumber(draft?.remaining_count)) {
                  draft.remaining_count = hasVoted
                    ? draft.remaining_count - 1
                    : draft.remaining_count + 1;
                }
              });
            },
          );
        }
      },
      onError: err => {
        toastError(err);
      },
    },
  );
};

export const useRemainingVote = (issueId?: string) => {
  const { address: currentAddress, userInfo } = useCurrentAddressInfo();
  const { data } = usePrimePostIssues({ enabled: true });
  const { primeIssueId } = useCurrentPrimeIssueId();
  const _issueId = issueId || primeIssueId;
  const currentIssue = useMemo(() => data?.find(i => String(i.id) === _issueId), [_issueId, data]);

  const url = '/user/prime_post/remaining_vote';
  return useQuery(
    [url, currentAddress, _issueId],
    () => request.get<{ remaining_count: number }>(url, { issue_id: _issueId }),
    { enabled: currentIssue?.status === 'ongoing' && !!userInfo?.web3_id },
  );
};
